import React from 'react';
import { IconButton, Menu, BottomSheet } from '@parsina/framework';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { CT } from '@parsina/framework/tokens';
import { MoreVertical } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// A پذیرش row's further ops (PM-0518) behind one ⋮ control: the kit `Menu` on a
// workstation, a `BottomSheet` on a phone — DESIGN_SYSTEM's "Anchored menu" row
// (a popover at the bottom edge of a phone is not a menu). The ops come from
// `useReceptionRowOps().opsFor(apt)`; the dangerous ones (عدم‌حضور، لغو نوبت)
// sit last, under a rule, so they are never the item a hurried click lands on.
//
// `inline` ops are the row's own buttons on the desktop («ثبت حضور»), so the
// menu leaves them out there; the phone card has no inline ones and lists all.
// ─────────────────────────────────────────────────────────────────────────────

function groups(ops) {
  return [ops.filter((o) => !o.danger), ops.filter((o) => o.danger)].filter((g) => g.length);
}

const Rule = () => <div role="separator" style={{ height: 1, background: C.border, margin: '4px 0' }} />;

export function RowOpsMenu({ ops, label, testId }) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const items = (ops || []).filter((o) => !o.inline);
  if (!items.length) return null;

  const row = (op) => (
    <button
      key={op.id}
      type="button"
      role="menuitem"
      disabled={op.disabled}
      aria-disabled={op.disabled || undefined}
      data-testid={`${testId}-op-${op.id}`}
      onClick={(e) => { e.stopPropagation(); setOpen(false); op.onSelect(); }}
      onMouseEnter={(e) => { e.currentTarget.style.background = CT.surface2; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      style={{
        display: 'flex', alignItems: 'center', width: '100%', minHeight: 36,
        padding: '8px 12px', border: 'none', borderRadius: 8, background: 'transparent',
        fontFamily: 'inherit', fontSize: '0.8125rem', fontWeight: 600, textAlign: 'start',
        color: op.danger ? CT.dangerInk : C.textPrimary,
        cursor: op.disabled ? 'progress' : 'pointer', opacity: op.disabled ? 0.6 : 1,
      }}
    >
      {op.label}
    </button>
  );

  return (
    <>
      <span ref={anchorRef} style={{ display: 'inline-flex', flexShrink: 0 }}>
        <IconButton
          variant="ghost"
          size={30}
          icon={<MoreVertical size={16} strokeWidth={2} aria-hidden />}
          label={label}
          aria-haspopup="menu"
          aria-expanded={open}
          data-testid={`${testId}-more`}
          onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        />
      </span>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        ariaLabel={label}
        width={220}
        placement="auto"
        data-testid={`${testId}-menu`}
      >
        <div style={{ padding: 4 }}>
          {groups(items).map((g, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Rule />}
              {g.map(row)}
            </React.Fragment>
          ))}
        </div>
      </Menu>
    </>
  );
}

export function RowOpsSheet({ ops, label, title, testId }) {
  const [open, setOpen] = React.useState(false);
  const items = ops || [];
  if (!items.length) return null;

  const row = (op) => (
    <button
      key={op.id}
      type="button"
      disabled={op.disabled}
      data-testid={`${testId}-op-${op.id}`}
      onClick={() => { setOpen(false); op.onSelect(); }}
      style={{
        display: 'flex', alignItems: 'center', width: '100%', minHeight: '3.25rem',
        padding: '0 1rem', border: 'none', borderRadius: '0.75rem', background: 'transparent',
        fontFamily: 'inherit', fontSize: '1rem', fontWeight: 600, textAlign: 'start',
        color: op.danger ? CT.dangerInk : C.textPrimary,
        opacity: op.disabled ? 0.6 : 1,
      }}
    >
      {op.label}
    </button>
  );

  return (
    <>
      <IconButton
        variant="ghost"
        icon={<MoreVertical size={20} strokeWidth={1.5} aria-hidden />}
        label={label}
        aria-haspopup="dialog"
        data-testid={`${testId}-more`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        onKeyDown={(e) => e.stopPropagation()}
      />
      {/* The sheet is portaled, but React still bubbles its clicks through the
          card — which would open the patient record under it. */}
      <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <BottomSheet
          open={open}
          onClose={() => setOpen(false)}
          title={title}
          snap={0.6}
          data-testid={`${testId}-sheet`}
        >
          <div role="list" aria-label={label} style={{ display: 'flex', flexDirection: 'column', paddingBottom: '0.5rem' }}>
            {groups(items).map((g, i) => (
              <React.Fragment key={i}>
                {i > 0 && <Rule />}
                {g.map(row)}
              </React.Fragment>
            ))}
          </div>
        </BottomSheet>
      </span>
    </>
  );
}

export default RowOpsMenu;
